/**
 * Каждая картинка нарезана в несколько размеров. В базе хранится только путь к оригиналу,
 * а вариант получается вставкой сегмента с размером перед именем файла:
 *
 *   species/elf.webp        — оригинал
 *   species/200/elf.webp    — превью в галерее (вписано в 200×200)
 *   species/400/elf.webp    — MainImage (400×400)
 *   species/900/elf.webp    — широкая картинка (900×900)
 *
 * Вариант существует всегда: если оригинал меньше, он просто скопирован без растягивания.
 * Оригинал запрашивается размером Number.POSITIVE_INFINITY.
 */
export const IMAGE_SIZES = [200, 400, 900, Number.POSITIVE_INFINITY] as const;

export type ImageSize = (typeof IMAGE_SIZES)[number];

/** Оригинал ничем не ограничен, поэтому на странице держим его не выше этого. */
export const ORIGINAL_MAX_HEIGHT = 1800;

/** Выше какой высоты показывать картинку: вариант вписан в size×size, значит и не выше size. */
export function imageMaxHeight(size: ImageSize): number {
	return Number.isFinite(size) ? size : ORIGINAL_MAX_HEIGHT;
}

/**
 * Самый маленький вариант, который целиком закрывает блок minWidth×minHeight (под object-fit: cover).
 * Вариант вписан в size×size, поэтому у узкой картинки мала ширина, у широкой — высота.
 * Если даже оригинал меньше блока, берём первый вариант, где он лежит без уменьшения.
 */
export function coverSize(
	width: number | null | undefined,
	height: number | null | undefined,
	minWidth: number,
	minHeight: number,
): ImageSize {
	if (!width || !height) return 400;
	for (const size of IMAGE_SIZES) {
		const scale = Math.min(1, size / Math.max(width, height));
		if ((width * scale >= minWidth && height * scale >= minHeight) || scale === 1) return size;
	}
	return Number.POSITIVE_INFINITY;
}

/**
 * srcset по нашим вариантам, когда размеры картинки неизвестны (обложка книги).
 * Ширина варианта не больше size, поэтому size и стоит дескриптором: браузер сам возьмёт нужный.
 */
export function variantSrcSet(path: string): string {
	return IMAGE_SIZES.filter((size) => Number.isFinite(size))
		.map((size) => `${imageUrl(path, size)} ${size}w`)
		.join(', ');
}

export function imageUrl(path: string, size?: ImageSize): string {
	if (!size || size === Number.POSITIVE_INFINITY) {
		return `/images/${path}`;
	}
	const slash = path.lastIndexOf('/');
	return `/images/${path.slice(0, slash + 1)}${size}/${path.slice(slash + 1)}`;
}
